import {Box, Radio, Typography} from '@mui/material';
import {t} from 'i18next';
import {isEmpty} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ReactComponent as InfoIcon} from '../../../../assests/svg/info_icon.svg';
import {
  getCustomMetaDataList,
  FieldDropDownType,
  getNumberTypeList,
} from '../../../../utils/fieldManagementUtils';
import AurButton from '../../../common/aur-button/AurButton';
import AurDrawerHeader from '../../../common/aur-drawer/AurDrawerHeader';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import {AurSelect} from '../../../common/aur-select/AurSelect';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import {ErrorDialog} from './ErrorDialog';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {
  RenameConfirmDialog,
  RenameConfirmDialogHandle,
} from './RenameConfirmDialog';

const regexValidationField = /^\w*$/;

export type FieldCategoryType =
  | 'TAG_FIELD'
  | 'STANDARD_METADATA'
  | 'CUSTOM_METADATA'
  | 'EXPORT'
  | 'STR';

export interface FieldValues {
  fieldName?: string;
  fieldType?: FieldDropDownType;
  isMultiValueField?: boolean;
  isRelationShipField?: boolean;
  isRelationalEditable?: boolean;
}

export interface IFieldViewProps {
  fieldFormTypes?: {
    isCreate: boolean;
    isEditForm: boolean;
    isView: boolean;
    isViewOnly: boolean;
  };
  fieldCategoryType: FieldCategoryType;
  values?: FieldValues;
  headerTitle: string;
  headerClass: string;
  headerSubtitle?: string;
  showError?: boolean;
  isEditingOrder?: boolean;
  dismissError?: () => void;
  onCancel?: () => void;
  onSave?: (values: FieldValues) => void;
  onRename?: (values: FieldValues) => void;
  renameButtonHidden?: boolean;
}

export const getRadioIcons = (
  isSelected: boolean,
  isDisabled: boolean
): string => {
  if (isSelected) {
    if (isDisabled) {
      return '/assests/images/radio-checked-disabled.svg';
    }
    return '/assests/images/radio-checked.svg';
  }
  return '/assests/images/radio-unchecked.svg';
};

const FieldView: React.FC<IFieldViewProps> = ({
  fieldFormTypes,
  fieldCategoryType,
  values,
  headerTitle,
  headerSubtitle,
  headerClass,
  showError,
  isEditingOrder = false,
  dismissError,
  onCancel,
  onSave,
  onRename,
  renameButtonHidden,
}) => {
  const [inputValue, setInputValue] = useState<string | undefined>(
    values?.fieldName
  );

  const [fieldType, setFieldType] = useState<FieldDropDownType | undefined>(
    values?.fieldType
  );

  const [isMultiValueField, setIsMultiValueField] = useState<boolean>(
    !!values?.isMultiValueField
  );

  const [isRelationShipField, setIsRelationShipField] = useState<boolean>(
    !!values?.isRelationShipField
  );

  const renameDialogRef = useRef<RenameConfirmDialogHandle | null>(null);

  const [numberType, setNumberType] = useState<FieldDropDownType | undefined>(
    values?.fieldType == FieldDropDownType.Integer ||
      values?.fieldType == FieldDropDownType.Float
      ? values.fieldType
      : undefined
  );

  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    setInputValue(values?.fieldName);
    setFieldType(values?.fieldType);
    setIsMultiValueField(!!values?.isMultiValueField);
    setIsRelationShipField(!!values?.isMultiValueField);
    setNumberType(
      values?.fieldType == FieldDropDownType.Integer ||
        values?.fieldType == FieldDropDownType.Float
        ? values.fieldType
        : undefined
    );
  }, [values]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(false);
      const value = e.target.value;
      setInputValue(value);
    },
    [setInputValue]
  );

  const validateTextField = useCallback(() => {
    setError(inputValue ? !regexValidationField.test(inputValue) : false);
  }, [inputValue]);

  const saveFieldName = useCallback(() => {
    !error &&
      onSave?.({
        fieldName: inputValue,
        fieldType:
          fieldType == FieldDropDownType.Integer ? numberType : fieldType,
        isMultiValueField: isMultiValueField,
        isRelationShipField: isRelationShipField,
      });
  }, [
    error,
    fieldType,
    inputValue,
    isMultiValueField,
    isRelationShipField,
    numberType,
    onSave,
  ]);

  const handleSave = useCallback(() => {
    validateTextField();
    if (fieldFormTypes?.isEditForm) {
      !error && renameDialogRef?.current?.show();
      return;
    }

    saveFieldName();
  }, [validateTextField, fieldFormTypes?.isEditForm, saveFieldName, error]);

  const handleRename = useCallback(() => {
    onRename?.({
      fieldName: inputValue,
      fieldType,
      isMultiValueField,
      isRelationShipField,
    });
  }, [fieldType, inputValue, isMultiValueField, isRelationShipField, onRename]);

  const handleCancel = useCallback(() => {
    if (fieldFormTypes?.isCreate && fieldCategoryType == 'CUSTOM_METADATA') {
      setFieldType(undefined);
      setInputValue('');
      setIsMultiValueField(false);
      setIsRelationShipField(false);
      setNumberType(undefined);
    } else {
      setError(false);
      setInputValue(values?.fieldName);
      onCancel?.();
    }
  }, [
    fieldCategoryType,
    fieldFormTypes?.isCreate,
    onCancel,
    values?.fieldName,
  ]);

  const handleChangeRelationshipValueField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsRelationShipField(event.target.value == 'true');
  };

  const isSaveDisabled = useMemo(() => {
    let returnValue = isEmpty(inputValue?.trim());
    if (fieldCategoryType == 'CUSTOM_METADATA') {
      const condition =
        returnValue ||
        isEmpty(fieldType) ||
        (values && values.fieldName && inputValue
          ? values.fieldName == inputValue
          : false);
      if (fieldType == FieldDropDownType.Integer) {
        returnValue = condition || isEmpty(numberType);
      } else {
        returnValue = condition;
      }
    }
    return returnValue;
  }, [inputValue, fieldCategoryType, fieldType, numberType]);

  const isMetaEditable = useMemo(() => {
    return !(fieldFormTypes?.isCreate || fieldFormTypes?.isEditForm);
  }, [fieldFormTypes?.isCreate, fieldFormTypes?.isEditForm]);

  const renderCreateButton = useCallback(() => {
    return [
      <div key={'exit-custom'} className="custom-field-update-buttons">
        <AurButton
          type={'exit'}
          label={
            fieldFormTypes?.isCreate && fieldCategoryType == 'CUSTOM_METADATA'
              ? t('buttons.clear')
              : t('buttons.cancel')
          }
          title={
            fieldCategoryType == 'CUSTOM_METADATA'
              ? t('buttons.clear')
              : t('buttons.cancel')
          }
          size="Medium"
          onClick={handleCancel}
          dataTestId="dontSaveButton-test"
          classNames="header-button"
          ariaLabel={fieldCategoryType == 'CUSTOM_METADATA' ? t('buttons.clearAndReturn'): t('buttons.cancelAndReturn')}
        />
        <AurButton
          type={'continue'}
          label={t('buttons.save')}
          title={t('buttons.save')}
          size="Medium"
          buttonType="submit"
          dataTestId="fieldName-saveButton"
          classNames="header-button"
          disabled={isSaveDisabled || error}
          onClick={handleSave}
          ariaLabel={t('fieldManagement.customField.saveButtonHint')}
        />
      </div>,
    ];
  }, [
    error,
    fieldCategoryType,
    fieldFormTypes?.isCreate,
    handleCancel,
    handleSave,
    isSaveDisabled,
  ]);

  const renderViewButton = useCallback(() => {
    if (renameButtonHidden) return [];
    return [
      <div key={'view-custom'} className="custom-field-update-buttons">
        <AurButton
          type={'exit'}
          label={t('buttons.rename')}
          title={t('buttons.rename')}
          size="Medium"
          dataTestId="renameSaveButton-test"
          classNames="header-button"
          onClick={handleRename}
          disabled={isEditingOrder}
          ariaLabel={t('fieldManagement.customField.renameFieldHint')}
        />
      </div>,
    ];
  }, [handleRename, isEditingOrder, renameButtonHidden]);

  const titleText = useMemo(() => {
    if (fieldFormTypes?.isView) {
      return inputValue ?? '';
    }

    if (fieldFormTypes?.isEditForm) {
      return t('fieldManagement.customField.renameField');
    }

    return headerTitle;
  }, [
    fieldFormTypes?.isView,
    fieldFormTypes?.isEditForm,
    inputValue,
    headerTitle,
  ]);

  const actionButtons = useMemo(() => {
    if (fieldFormTypes?.isViewOnly) {
      return [];
    }

    if (fieldFormTypes?.isView) {
      return renderViewButton();
    }

    return renderCreateButton();
  }, [
    fieldFormTypes?.isViewOnly,
    fieldFormTypes?.isView,
    renderViewButton,
    renderCreateButton,
  ]);

  /*
  const handleChangeMultiValueField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsMultiValueField(event.target.value == 'true');
  };
  */

  return (
    <>
      <AurDrawerHeader
        title={titleText}
        titleCssClass={`${headerClass} field-view-container`}
        buttons={actionButtons}
      />
      {(fieldFormTypes?.isView || fieldFormTypes?.isViewOnly) && (
        <Box component="sub" className={'aur-drawer-header-subtitle-container'}>
          <Typography className="typo-text body3_size color-mist-400 aur-drawer-header-subtitle">
            {headerSubtitle?.toUpperCase()}
          </Typography>
          {fieldFormTypes.isViewOnly && (
            <AurButton
              size={'Small'}
              classNames="view-only-container"
              startIcon={
                <AurIcon
                  icon={IconNames.RemoveRedEye}
                  color={IconColors.Secondary}
                />
              }
              label={t('fieldManagement.customField.viewOnly')}
              type={'exit'}
            />
          )}
        </Box>
      )}
      {!fieldFormTypes?.isView && (
        <Box className={'custom-field-input-container'}>
          <AurTextField
            value={inputValue}
            label={t('fieldManagement.customField.fieldNameLabel')}
            placeholder={
              fieldCategoryType == 'CUSTOM_METADATA'
                ? t(
                    'fieldManagement.customField.customMetaDataFieldPlaceholder'
                  )
                : t('fieldManagement.customField.customTagFieldPlaceholder')
            }
            ariaLabel={
              fieldCategoryType == 'CUSTOM_METADATA'
                ? t(
                    'fieldManagement.customField.customMetaDataFieldPlaceholder'
                  )
                : t('fieldManagement.customField.customTagFieldPlaceholder')
            }
            helperText={t('common.fieldTagValidation')}
            variant="filled"
            fullWidth
            minRows={1}
            className={`aur-textfield custom-text-field text-input-field`}
            inputProps={{maxLength: 50}}
            error={error}
            onChange={handleChange}
            onBlur={validateTextField}
          />
        </Box>
      )}
      {fieldCategoryType == 'CUSTOM_METADATA' && fieldFormTypes?.isCreate ? (
        <Box className="custom-meta-data-wrapper">
          <Box className="custom-meta-data-field">
            <AurSelect
              dataTestId="field-type-dropdown"
              label={t('fieldManagement.customField.fieldType')}
              optionList={getCustomMetaDataList()}
              key={fieldType}
              isSingleSelection
              field={{
                value: fieldType,
                onChange: (event: any) => {
                  setFieldType(event.target.value);
                },
              }}
              fieldState={{}}
              style={{width: '480px'}}
              disabled={isMetaEditable}
            />
          </Box>
          {fieldType == FieldDropDownType.Text ? (
            <>
              {/* <Box className="field-radio-wrapper">
                <Box className="field-radio-label">
                  <Typography
                    component="div"
                    className="typo-text subhead3_size label"
                  >
                    {t('fieldManagement.customField.isThisAMultiValueField')}
                  </Typography>
                  <AurTooltip
                    title={'TBD'}
                    aria-label={t('eca.flyout.savedSearch.tooltip.copyButton')}
                    className="tooltip-content typo-text label3_size"
                    disableInteractive
                  >
                    <InfoIcon className="info-icon" />
                  </AurTooltip>
                </Box>
                <Box className="field-radio-group">
                  <Box className="field-radio-item">
                    <Radio
                      onChange={handleChangeMultiValueField}
                      checked={isMultiValueField}
                      name="multi-value-radio"
                      value={true}
                      disabled={isRelationShipField || isMetaEditable}
                      checkedIcon={
                        <img
                          src={getRadioIcons(
                            isMultiValueField,
                            isRelationShipField || isMetaEditable
                          )}
                        />
                      }
                    />
                    <Typography
                      component="span"
                      className="typo-text subhead3_size"
                    >
                      {t('fieldManagement.customField.yes')}
                    </Typography>
                  </Box>
                  <Box className="field-radio-item">
                    <Radio
                      onChange={handleChangeMultiValueField}
                      checked={!isMultiValueField}
                      name="multi-value-radio"
                      value={false}
                      disabled={isRelationShipField || isMetaEditable}
                      checkedIcon={
                        <img
                          src={getRadioIcons(
                            !isMultiValueField,
                            isRelationShipField || isMetaEditable
                          )}
                        />
                      }
                    />
                    <Typography
                      component="span"
                      className="typo-text subhead3_size"
                    >
                      {t('fieldManagement.customField.no')}
                    </Typography>
                  </Box>
                </Box>
              </Box> */}
              {!isMultiValueField && (
                <Box className="field-radio-wrapper">
                  <Box className="field-radio-label">
                    <Typography
                      component="div"
                      className="typo-text subhead3_size label"
                    >
                      {t('fieldManagement.customField.isRelationShipField')}
                    </Typography>
                    <AurTooltip
                      title={t(
                        'fieldManagement.customField.relationshipFieldTooltip'
                      )}
                      aria-label={t(
                        'eca.flyout.savedSearch.tooltip.copyButton'
                      )}
                      className="tooltip-content typo-text label3_size"
                      disableInteractive
                    >
                      <InfoIcon className="info-icon" />
                    </AurTooltip>
                  </Box>
                  <Box className="field-radio-group">
                    <Box className="field-radio-item">
                      <Radio
                        onChange={handleChangeRelationshipValueField}
                        checked={isRelationShipField}
                        name="relationship-value-radio"
                        disabled={isMetaEditable}
                        value={true}
                        checkedIcon={
                          <img
                            alt={''}
                            src={getRadioIcons(
                              isRelationShipField,
                              isMultiValueField || isMetaEditable
                            )}
                          />
                        }
                      />
                      <Typography
                        component="span"
                        className="typo-text subhead3_size"
                      >
                        {t('fieldManagement.customField.yes')}
                      </Typography>
                    </Box>
                    <Box className="field-radio-item">
                      <Radio
                        onChange={handleChangeRelationshipValueField}
                        checked={!isRelationShipField}
                        name="relationship-value-radio"
                        value={false}
                        disabled={isMetaEditable}
                        checkedIcon={
                          <img
                            src={getRadioIcons(
                              !isRelationShipField,
                              isMultiValueField || isMetaEditable
                            )}
                          />
                        }
                      />
                      <Typography
                        component="span"
                        className="typo-text subhead3_size"
                      >
                        {t('fieldManagement.customField.no')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          ) : null}
          {fieldType == FieldDropDownType.Integer ? (
            <Box className="custom-meta-data-field">
              <AurSelect
                dataTestId="number-type-dropdown"
                label={t('fieldManagement.customField.numberType')}
                optionList={getNumberTypeList()}
                key={numberType}
                field={{
                  value: numberType,
                  onChange: (event: any) => {
                    setNumberType(event.target.value);
                  },
                }}
                fieldState={{}}
                style={{width: '480px'}}
                disabled={isMetaEditable}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
      {showError && dismissError && (
        <ErrorDialog
          open={showError}
          title={t('fieldManagement.customField.duplicateErrorField.title')}
          description={t(
            'fieldManagement.customField.duplicateErrorField.description'
          )}
          button={t('common.okGotIt')}
          onOkClicked={dismissError}
        />
      )}
      <RenameConfirmDialog ref={renameDialogRef} onConfirm={saveFieldName} />
    </>
  );
};

export {FieldView};
